import { yupResolver } from "@hookform/resolvers/yup";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { MdmFormProps } from "../../Forms/MdmFormProps";
import PageHeader from "../../Layout/Header/PageHeader";
import PageHeaderButton from "../../Layout/Header/PageHeaderButton";
import PageHeaderIconButton from "../../Layout/Header/PageHeaderIconButton";
import { MdmTabs } from "../../Layout/Tabs/MdmTabs";
import { Edc } from "../Edc";
import { edcDefaultValues } from "./edcDefaultValues";
import { EdcForm } from "./EdcForm/EdcForm";
import { EdcOptionalForm } from "./EdcOptionalForm/EdcOptionalForm";
import { edcSchema } from "./edcSchema";
import { confirm } from "../../Layout/Dialog/ConfirmDialog";
import { resetFormStateAndKeepFormValues } from "../../Forms/resetFormStateAndKeepFormValues";
import { ADDRESS_OPTIONAL, INFORMATION } from "../../Layout/Tabs/MdmTabPanelConstants";
import { isFormSubmitForbidden } from "../../Forms/isFormSubmitForbidden";
import { useIsUserInRoles } from "../../Auth/useIsUserInRoles";
import { AuthorizationPolicies } from "../../Auth/AuthorizationPolicy";

type Props = {
  title: string;
  edc: Edc | undefined;
  onSave: (edc: Edc) => void;
  onDelete?: (edcId: number | null | undefined) => void;
};

export const EdcFormTabs = ({
  title,
  edc,
  onSave,
  disabledFields,
  onDelete,
}: Props & MdmFormProps) => {
  const isUserInAdminNetworkRole = useIsUserInRoles(AuthorizationPolicies.RequireAdminNetworkRoles);

  const methods = useForm<Edc>({
    defaultValues: edcDefaultValues,
    resolver: yupResolver(edcSchema),
    mode: "all",
    disabled: !isUserInAdminNetworkRole,
  });

  useEffect(() => {
    methods.reset(edc);

    // Cleanup
    return () => methods.reset(edcDefaultValues);
  }, [edc, methods, methods.reset]);

  const onDeleteWithConfirmation = async () => {
    const isConfirmed = await confirm(`Do you really want to delete EDC with Id ${edc?.Id}?`);
    if (!isConfirmed || !onDelete) {
      return;
    }
    await onDelete(edc?.Id);
  };

  return (
    <>
      <PageHeader headerText={title}>
        {onDelete !== undefined && (
          <PageHeaderIconButton
            onClick={() => onDeleteWithConfirmation()}
            color="secondary"
            disabled={!isUserInAdminNetworkRole}
          >
            <DeleteIcon />
          </PageHeaderIconButton>
        )}
        <PageHeaderButton
          onClick={methods.handleSubmit((values: Edc) => {
            onSave(values);
            resetFormStateAndKeepFormValues(methods);
          })}
          disabled={isFormSubmitForbidden(methods.formState)}
        >
          Save
        </PageHeaderButton>
      </PageHeader>

      <FormProvider {...methods}>
        <MdmTabs
          tabs={[
            {
              index: 0,
              label: INFORMATION,
              content: <EdcForm disabledFields={disabledFields} />,
            },
            {
              index: 1,
              label: ADDRESS_OPTIONAL,
              content: <EdcOptionalForm disabledFields={disabledFields} />,
              disabled: !edc,
            },
          ]}
        />
      </FormProvider>
    </>
  );
};
